import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import NColumnTable from "@/charts/NColumnTable";
import useLoadData from "@/hooks/useLoadData";
import CustomDropdown from "@/components/ui/CustomDropdown";
import PasswordInput from "@/components/ui/PasswordInput";
import { showApiError, showSuccess } from "@/utils";
import apiClient from "@/services/apiClient";
import { BACKEND_URL, AE_BACKEND_URL } from "@/config";

const AEClientModal = ({
  isEdit = false,
  formData,
  handleInputChange,
  isSubmitting,
  onClose,
  handleSubmit,
  onDelete,
  allClients,
  allClientsLoaded,
}) => {
  const { t } = useTranslation();
  const modalTitle = isEdit
    ? t("superAdmin.aeClients.editTitle", "Edit Answer Engine Client Config")
    : t("superAdmin.aeClients.addTitle", "Add Answer Engine Client Config");

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-bold text-dark">{modalTitle}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={isSubmitting}
              aria-label={t("common.close", "Close")}
            ></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="ae-client-id" className="form-label fw-bold">
                    {t("superAdmin.clients.clientId", "Client ID")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  {isEdit ? (
                    <input
                      type="text"
                      className="form-control bg-light"
                      id="ae-client-id"
                      name="client_id"
                      value={formData.client_id}
                      readOnly
                      disabled
                      style={{ lineHeight: "1.5" }}
                    />
                  ) : (
                    <>
                      <CustomDropdown
                        triggerId="ae-client-id"
                        options={[
                          { value: "", label: t("superAdmin.aeClients.selectClientId", "Select an existing client...") },
                          ...(allClientsLoaded && allClients
                            ? allClients.map((c) => ({
                                value: c.client_id,
                                label: c.client_id,
                              }))
                            : []),
                        ]}
                        value={formData.client_id}
                        onChange={(val) =>
                          handleInputChange({
                            target: { name: "client_id", value: val },
                          })
                        }
                        disabled={isSubmitting}
                      />
                      <label htmlFor="ae-custom-client-id" className="mt-2 small text-muted fw-bold d-block">
                        {t("superAdmin.aeClients.customIdHint", "Or type a custom Client ID:")}
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm mt-1"
                        id="ae-custom-client-id"
                        name="client_id"
                        value={formData.client_id}
                        onChange={handleInputChange}
                        placeholder={t("superAdmin.aeClients.enterCustomId", "Enter custom Client ID")}
                        disabled={isSubmitting}
                        style={{ lineHeight: "1.5" }}
                      />
                    </>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="ae-client-name" className="form-label fw-bold">
                    {t("superAdmin.aeClients.clientName", "Client Name")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="ae-client-name"
                    name="client_name"
                    value={formData.client_name}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    placeholder="e.g. Acme Corp"
                    style={{ lineHeight: "1.5" }}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-12 mb-3">
                  <label htmlFor="ae-client-email" className="form-label fw-bold">
                    {t("superAdmin.aeClients.clientEmail", "Client Email")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    id="ae-client-email"
                    name="client_email"
                    value={formData.client_email}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    placeholder="admin@acme.com"
                    style={{ lineHeight: "1.5" }}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="ae-homepage-url" className="form-label fw-bold">
                    {t("superAdmin.aeClients.homepageUrl", "Homepage URL")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="url"
                    className="form-control"
                    id="ae-homepage-url"
                    name="homepage_url"
                    value={formData.homepage_url}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    placeholder="https://acme.com"
                    style={{ lineHeight: "1.5" }}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="ae-snippets-key" className="form-label fw-bold">
                    {t("superAdmin.aeClients.snippetsApiKey", "Snippets API Key")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <PasswordInput
                    id="ae-snippets-key"
                    name="snippets_api_key"
                    value={formData.snippets_api_key}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    placeholder="e.g. snip-key-abc"
                    style={{ lineHeight: "1.5" }}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="ae-elastic-index" className="form-label fw-bold">
                    {t("superAdmin.aeClients.elasticIndex", "Elastic Index")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="ae-elastic-index"
                    name="elastic_index"
                    value={formData.elastic_index}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    placeholder="e.g. acme-index"
                    style={{ lineHeight: "1.5" }}
                  />
                </div>
                <div className="col-md-6 mb-3 d-flex align-items-center">
                  <div className="form-check form-switch mt-4">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="ae-pii-redaction"
                      name="PII_redaction_enabled"
                      checked={formData.PII_redaction_enabled}
                      onChange={(e) =>
                        handleInputChange({
                          target: { name: "PII_redaction_enabled", value: e.target.checked },
                        })
                      }
                      disabled={isSubmitting}
                      style={{ cursor: "pointer" }}
                    />
                    <label htmlFor="ae-pii-redaction" className="form-check-label fw-bold" style={{ cursor: "pointer" }}>
                      {t("superAdmin.aeClients.piiRedactionEnabled", "PII Redaction Enabled")}
                    </label>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="ae-about-url" className="form-label fw-bold">
                    {t("superAdmin.aeClients.aboutUrl", "About URL")}
                  </label>
                  <input
                    type="url"
                    className="form-control"
                    id="ae-about-url"
                    name="about_url"
                    value={formData.about_url || ""}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    placeholder="https://acme.com/about"
                    style={{ lineHeight: "1.5" }}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="ae-contact-url" className="form-label fw-bold">
                    {t("superAdmin.aeClients.contactUrl", "Contact URL")}
                  </label>
                  <input
                    type="url"
                    className="form-control"
                    id="ae-contact-url"
                    name="contact_url"
                    value={formData.contact_url || ""}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    placeholder="https://acme.com/contact"
                    style={{ lineHeight: "1.5" }}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-between">
              <div>
                {isEdit && (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={onDelete}
                    disabled={isSubmitting}
                  >
                    <i className="bi bi-trash me-1"></i>
                    {t("common.delete", "Delete")}
                  </button>
                )}
              </div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  {t("common.cancel", "Cancel")}
                </button>
                <button
                  type="submit"
                  className="btn text-white"
                  disabled={isSubmitting || !formData.client_id || !formData.client_name || !formData.client_email || !formData.homepage_url || !formData.snippets_api_key || !formData.elastic_index}
                  style={{ backgroundColor: "#e63374" }}
                >
                  {isSubmitting ? t("common.submitting", "Saving...") : t("common.save", "Save")}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default function SuperAdminAEClient() {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingClientId, setLoadingClientId] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [formData, setFormData] = useState({
    client_id: "",
    client_name: "",
    client_email: "",
    homepage_url: "",
    about_url: "",
    contact_url: "",
    snippets_api_key: "",
    elastic_index: "",
    PII_redaction_enabled: false,
  });

  const [allClients, allClientsLoaded, allClientErrors] = useLoadData(
    `${BACKEND_URL}/api/v1/client/all?refresh=${refreshTrigger}`,
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfigureAE = useCallback(async (client_id) => {
    setLoadingClientId(client_id);
    try {
      const response = await apiClient.get(
        `${AE_BACKEND_URL}/admin/client-config/${encodeURIComponent(client_id)}`,
      );
      const config = response.data;
      setFormData({
        client_id: config.client_id || client_id,
        client_name: config.client_name || "",
        client_email: config.client_email || "",
        homepage_url: config.homepage_url || "",
        about_url: config.about_url || "",
        contact_url: config.contact_url || "",
        snippets_api_key: config.snippets_api_key || "",
        elastic_index: config.elastic_index || "",
        PII_redaction_enabled: !!config.PII_redaction_enabled,
      });
      setIsEdit(true);
      setShowModal(true);
    } catch (err) {
      if (err.response?.status === 404) {
        // Not found -> open creation modal with ID prefilled
        setFormData({
          client_id: client_id,
          client_name: "",
          client_email: "",
          homepage_url: "",
          about_url: "",
          contact_url: "",
          snippets_api_key: "",
          elastic_index: "",
          PII_redaction_enabled: false,
        });
        setIsEdit(false);
        setShowModal(true);
      } else if (!err.response) {
        showApiError(
          t(
            "superAdmin.aeClients.networkError",
            "Network error occurred while fetching configuration.",
          ),
        );
      } else {
        showApiError(
          err.response?.data?.detail ||
            err.response?.data?.message ||
            t(
              "superAdmin.aeClients.loadError",
              "Failed to load Answer Engine configuration.",
            ),
        );
      }
    } finally {
      setLoadingClientId(null);
    }
  }, [t]);

  const handleCreateAEBlank = () => {
    setFormData({
      client_id: "",
      client_name: "",
      client_email: "",
      homepage_url: "",
      about_url: "",
      contact_url: "",
      snippets_api_key: "",
      elastic_index: "",
      PII_redaction_enabled: false,
    });
    setIsEdit(false);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const url = isEdit
        ? `${AE_BACKEND_URL}/admin/client-config/${encodeURIComponent(formData.client_id)}`
        : `${AE_BACKEND_URL}/admin/client-config/`;

      const payload = { ...formData };
      if (isEdit) {
        // client_id is read-only path param and not updated
        delete payload.client_id;
      }

      if (isEdit) {
        await apiClient.put(url, payload);
      } else {
        await apiClient.post(url, payload);
      }

      showSuccess(
        isEdit
          ? t("superAdmin.aeClients.updateSuccess", "Configuration updated successfully!")
          : t("superAdmin.aeClients.createSuccess", "Configuration created successfully!")
      );
      setShowModal(false);
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      if (!err.response) {
        showApiError(
          t(
            "superAdmin.aeClients.networkSaveError",
            "Network error occurred while saving configuration.",
          ),
        );
      } else {
        showApiError(
          err.response?.data?.detail ||
            err.response?.data?.message ||
            t(
              "superAdmin.aeClients.saveError",
              "Failed to save configuration.",
            ),
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const filteredClients = useMemo(() => {
    if (!allClients) return [];
    // Fail-closed filter: only show clients with is_ae_client explicitly set to true
    let clients = allClients.filter((c) => c.is_ae_client === true);

    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      clients = clients.filter(
        (c) =>
          c.client_id?.toLowerCase().includes(s) ||
          c.own_brand?.toLowerCase().includes(s) ||
          c.pricing_model?.toLowerCase().includes(s)
      );
    }
    return clients.map((c) => {
      const isCurrentLoading = loadingClientId === c.client_id;
      return {
        ...c,
        actions: (
          <button
            className="btn btn-sm text-white px-3 d-flex align-items-center gap-1 mx-auto"
            style={{ backgroundColor: "#e63374", borderRadius: "6px" }}
            onClick={() => handleConfigureAE(c.client_id)}
            disabled={loadingClientId !== null}
          >
            {isCurrentLoading ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            ) : (
              <i className="bi bi-sparkles"></i>
            )}
            {t("superAdmin.aeClients.configure", "Configure")}
          </button>
        ),
      };
    });
  }, [allClients, searchTerm, loadingClientId, handleConfigureAE, t]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="h5 fw-bold text-dark mb-1">
            {t("superAdmin.aeClients.title", "Answer Engine Client Configurations")}
          </h4>
          <p className="text-muted small mb-0">
            {t("superAdmin.aeClients.subtitle", "Manage Answer Engine specific settings like Elasticsearch indexing and PII redaction.")}
          </p>
        </div>
        <button
          className="btn btn-light d-flex align-items-center gap-1"
          style={{ color: "#e63374", borderColor: "#e63374" }}
          onClick={handleCreateAEBlank}
        >
          <i className="bi bi-plus-circle"></i>
          {t("superAdmin.aeClients.addBtn", "Add AE Config")}
        </button>
      </div>

      <div className="mb-3 d-flex align-items-center gap-3">
        <input
          type="text"
          className="form-control"
          placeholder={t("superAdmin.clients.searchPlaceholder", "Search clients...")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: 500 }}
        />
      </div>

      <NColumnTable
        data={filteredClients}
        n={4}
        columnKeys={["client_id", "own_brand", "pricing_model", "actions"]}
        columnTitles={[
          t("superAdmin.clients.clientId", "Client ID"),
          t("superAdmin.clients.ownBrand", "Own Brand"),
          t("superAdmin.clients.pricingModel", "Pricing Model"),
          t("common.actions", "Actions"),
        ]}
        columnRatio="25-25-25-25"
        columnAlignment="l-l-l-c"
        isLoaded={allClientsLoaded}
        error={allClientErrors}
        pagination={true}
        itemsPerPage={10}
      />

      {showModal && (
        <AEClientModal
          isEdit={isEdit}
          formData={formData}
          handleInputChange={handleInputChange}
          isSubmitting={isSubmitting}
          onClose={() => setShowModal(false)}
          handleSubmit={handleSubmit}
          onDelete={handleDelete}
          allClients={allClients}
          allClientsLoaded={allClientsLoaded}
        />
      )}

      {showDeleteConfirm && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1100 }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow" style={{ borderRadius: "8px" }}>
              <div className="modal-header border-bottom-0 pb-0">
                <h5 className="modal-title fw-bold text-dark">
                  {t("superAdmin.aeClients.confirmDeleteTitle", "Confirm Delete")}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteConfirm(false)}
                  aria-label={t("common.close", "Close")}
                ></button>
              </div>
              <div className="modal-body py-4">
                <p className="mb-0 text-secondary">
                  {t("superAdmin.aeClients.deleteConfirm", "Are you sure you want to delete configuration for {{id}}?", { id: formData.client_id })}
                </p>
              </div>
              <div className="modal-footer border-top-0 pt-0">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isSubmitting}
                >
                  {t("common.cancel", "Cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  data-testid="confirm-delete-btn"
                  onClick={async () => {
                    setIsSubmitting(true);
                    try {
                      await apiClient.delete(
                        `${AE_BACKEND_URL}/admin/client-config/${encodeURIComponent(formData.client_id)}`
                      );
                      showSuccess(t("superAdmin.aeClients.deleteSuccess", "Configuration deleted successfully!"));
                      setShowDeleteConfirm(false);
                      setShowModal(false);
                      setRefreshTrigger((prev) => prev + 1);
                    } catch (err) {
                      if (!err.response) {
                        showApiError(
                          t(
                            "superAdmin.aeClients.networkDeleteError",
                            "Network error occurred while deleting configuration.",
                          ),
                        );
                      } else {
                        showApiError(
                          err.response?.data?.detail ||
                            err.response?.data?.message ||
                            t(
                              "superAdmin.aeClients.deleteError",
                              "Failed to delete configuration.",
                            ),
                        );
                      }
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  ) : (
                    t("common.delete", "Delete")
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
